/**
 * InteligenciaHidrica.js v4.4.32 — Biblioteca de referencia del sector agua/presas.
 *
 * Vista unificada de oportunidades_placsp + archivadas + licitaciones_abiertas
 * filtrando por sector hídrico. Orientada a:
 *   - Buscar precedentes de pliegos (PE, auscultación, CH)
 *   - Mapear organismos activos y volumen histórico
 *   - Localizar referencias para briefings INETUM/partners
 *
 * Endpoints consumidos:
 *   GET /api/inteligencia-hidrica/entries    (filtrable, paginable)
 *   GET /api/inteligencia-hidrica/stats      (KPIs)
 *   GET /api/inteligencia-hidrica/categorias (catálogo dropdown)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/services/api";
import {
  Droplets,
  Waves,
  Building2,
  Euro,
  RefreshCw,
  FilterX,
  Search,
  Loader2,
  ExternalLink,
  Archive,
  Clock,
  CheckCircle2,
  Download,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import DateRangeFilter from "@/components/DateRangeFilter";


const SOURCE_LABELS = {
  placsp: { label: "Activa", icon: CheckCircle2, className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40" },
  abierta: { label: "Abierta", icon: Clock, className: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40" },
  archivada: { label: "Archivada", icon: Archive, className: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40" },
};

const CATEGORIA_COLORS = {
  PLAN_EMERGENCIA: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40",
  AUSCULTACION: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40",
  SEGURIDAD_PRESA: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40",
  INUNDACIONES: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/40",
  OBRAS_HIDRAULICAS: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/40",
  CONSULTORIA_HIDRAULICA: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/40",
  MANTENIMIENTO_CH: "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 border-yellow-500/40",
  OTROS: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30",
};

const formatEUR = (n) =>
  n
    ? new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const formatFecha = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  // Ya está en YYYY-MM-DD, reformatear a DD/MM/YYYY
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

// Exporta entries a CSV (Excel-friendly: BOM UTF-8 + ; separator)
const exportarCSV = (entries) => {
  if (!entries?.length) return;
  const cols = [
    "fecha", "estado", "categoria", "titulo", "organismo",
    "cpv", "importe", "score", "expediente_id", "url",
  ];
  const escapeCSV = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[";\n,]/.test(s) ? `"${s}"` : s;
  };
  const header = cols.join(";");
  const rows = entries.map((e) => [
    String(e.fecha_referencia || "").slice(0, 10),
    e._source,
    e.categoria_agua,
    escapeCSV(e.titulo),
    escapeCSV(e.organismo),
    e.cpv,
    e.importe,
    e.score,
    escapeCSV(e.expediente_id),
    e.url,
  ].map(escapeCSV).join(";"));
  const csv = "﻿" + [header, ...rows].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const ts = new Date().toISOString().slice(0, 10);
  a.download = `inteligencia-hidrica-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


export default function InteligenciaHidrica() {
  // Filtros
  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [source, setSource] = useState("todas");
  const [organismo, setOrganismo] = useState("");
  const [sort, setSort] = useState("fecha_desc");
  // v4.4.33 — filtro fecha (fecha_referencia, server soporta fecha_desde/hasta)
  const [fechaRango, setFechaRango] = useState({ desde: null, hasta: null });

  // Data
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // Cargar catálogo de categorías y stats al montar
  useEffect(() => {
    api.get("/inteligencia-hidrica/categorias")
      .then((r) => setCategorias(r.data?.categorias || []))
      .catch(() => setCategorias([]));
    api.get("/inteligencia-hidrica/stats")
      .then((r) => setStats(r.data))
      .catch((e) => {
        console.error("Error stats:", e);
        toast.error("Error cargando KPIs");
      });
  }, []);

  // Cargar entries con filtros
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        sort,
      };
      if (categoria !== "todas") params.categoria = categoria;
      if (source !== "todas") params.source = source;
      if (organismo.trim()) params.organismo = organismo.trim();
      if (search.trim()) params.q = search.trim();
      // v4.4.33 — rango fechas
      if (fechaRango.desde) params.fecha_desde = fechaRango.desde;
      if (fechaRango.hasta) params.fecha_hasta = fechaRango.hasta;

      const resp = await api.get("/inteligencia-hidrica/entries", { params });
      setEntries(resp.data?.entries || []);
      setTotal(resp.data?.total || 0);
    } catch (error) {
      console.error("Error loading entries:", error);
      toast.error("Error al cargar entries");
    } finally {
      setLoading(false);
    }
  }, [categoria, source, organismo, search, sort, page, fechaRango]);

  // Reset page cuando cambian filtros
  useEffect(() => {
    setPage(0);
  }, [categoria, source, organismo, search, sort, fechaRango]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const clearFiltros = () => {
    setCategoria("todas");
    setSource("todas");
    setOrganismo("");
    setSearch("");
    setSort("fecha_desc");
    setFechaRango({ desde: null, hasta: null });
  };

  const hasFiltros = useMemo(
    () =>
      categoria !== "todas" ||
      source !== "todas" ||
      organismo.trim() !== "" ||
      search.trim() !== "" ||
      sort !== "fecha_desc" ||
      fechaRango.desde ||
      fechaRango.hasta,
    [categoria, source, organismo, search, sort, fechaRango]
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Top organismos: limpiar prefijos repetitivos para mostrar más legible
  const topOrganismos = useMemo(() => {
    if (!stats?.top_organismos) return [];
    return stats.top_organismos.slice(0, 12).map((o) => ({
      ...o,
      label: (o.organismo || "")
        .replace(/^Presidencia de la /, "")
        .replace(/^Consejo de Administración de /, "")
        .replace(/^Consejero Delegado de /, ""),
    }));
  }, [stats]);

  // Evolución por año: filtrar años con poco volumen para gráfico legible
  const datosEvolucion = useMemo(() => {
    if (!stats?.por_año) return [];
    return stats.por_año
      .filter((y) => parseInt(y.año) >= 2020)
      .map((y) => ({ año: y.año, count: y.count }));
  }, [stats]);

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-cyan-500/15 border border-cyan-500/40">
            <Droplets className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold theme-text">Inteligencia Hídrica</h1>
            <p className="theme-text-muted text-sm">
              Biblioteca unificada del sector agua: planes de emergencia,
              auscultación, confederaciones hidrográficas, obras hidráulicas.
              Incluye activas, licitaciones abiertas y archivo histórico.
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Waves className="w-3.5 h-3.5" /> Total sector agua
            </div>
            <div className="text-2xl font-bold theme-text">
              {stats?.total_entries ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Activas
            </div>
            <div className="text-2xl font-bold theme-text">
              {stats?.por_source?.placsp ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Clock className="w-3.5 h-3.5 text-blue-500" /> Abiertas
            </div>
            <div className="text-2xl font-bold theme-text">
              {stats?.por_source?.abierta ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Archive className="w-3.5 h-3.5" /> Archivadas
            </div>
            <div className="text-2xl font-bold theme-text">
              {stats?.por_source?.archivada ?? "—"}
            </div>
          </CardContent>
        </Card>
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-1">
              <Euro className="w-3.5 h-3.5" /> Adjudicado histórico
            </div>
            <div className="text-xl font-bold theme-text">
              {stats?.importe_adjudicado ? formatEUR(stats.importe_adjudicado) : "—"}
            </div>
            <div className="text-xs theme-text-muted">(dedup)</div>
          </CardContent>
        </Card>
      </div>

      {/* === Evolución temporal + Top organismos === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 print:hidden">
        {/* Gráfico evolución por año */}
        <Card className="theme-card lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-3">
              <TrendingUp className="w-3.5 h-3.5" /> Evolución temporal del sector
            </div>
            {datosEvolucion.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={datosEvolucion} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                  <XAxis dataKey="año" stroke="currentColor" className="theme-text-muted text-xs" />
                  <YAxis stroke="currentColor" className="theme-text-muted text-xs" />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "rgba(15,23,42,0.95)",
                      border: "1px solid rgba(148,163,184,0.3)",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: "#fff",
                    }}
                    formatter={(v) => [`${v} contratos`, "Volumen"]}
                  />
                  <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm theme-text-muted py-8 text-center">
                Sin datos suficientes para mostrar evolución.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top organismos clickable */}
        <Card className="theme-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 theme-text-muted text-xs uppercase mb-3">
              <Building2 className="w-3.5 h-3.5" /> Top organismos
            </div>
            {topOrganismos.length > 0 ? (
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {topOrganismos.map((o) => {
                  const activo = organismo && o.organismo.toLowerCase().includes(organismo.toLowerCase());
                  return (
                    <button
                      key={o.organismo}
                      onClick={() => {
                        setOrganismo(activo ? "" : o.organismo);
                      }}
                      className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded transition text-xs ${
                        activo
                          ? "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/40"
                          : "hover:theme-bg-tertiary theme-text border border-transparent"
                      }`}
                      title={o.organismo}
                    >
                      <span className="truncate flex-1">{o.label}</span>
                      <span className="text-xs font-mono theme-text-muted shrink-0">{o.count}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm theme-text-muted py-4 text-center">
                Sin datos.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-4 theme-card print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs theme-text-muted mb-1 block">
                Búsqueda en título
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 theme-text-muted" />
                <Input
                  placeholder="presa, auscultación, aliviadero..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="min-w-[220px]">
              <label className="text-xs theme-text-muted mb-1 block">
                Categoría hídrica
              </label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="theme-bg-tertiary theme-border theme-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas las categorías</SelectItem>
                  {categorias.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[200px]">
              <label className="text-xs theme-text-muted mb-1 block">
                Organismo (contiene)
              </label>
              <Input
                placeholder="CH Ebro, TRAGSA, DG Agua..."
                value={organismo}
                onChange={(e) => setOrganismo(e.target.value)}
              />
            </div>

            <div className="min-w-[160px]">
              <label className="text-xs theme-text-muted mb-1 block">Estado</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="theme-bg-tertiary theme-border theme-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todos</SelectItem>
                  <SelectItem value="placsp">Activas</SelectItem>
                  <SelectItem value="abierta">Abiertas</SelectItem>
                  <SelectItem value="archivada">Archivadas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="text-xs theme-text-muted mb-1 block">Ordenar</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="theme-bg-tertiary theme-border theme-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fecha_desc">Fecha ↓ (recientes)</SelectItem>
                  <SelectItem value="fecha_asc">Fecha ↑ (antiguas)</SelectItem>
                  <SelectItem value="importe_desc">Importe ↓</SelectItem>
                  <SelectItem value="importe_asc">Importe ↑</SelectItem>
                  <SelectItem value="score_desc">Score ↓</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* v4.4.33 — Filtro fecha (fecha_referencia) */}
            <div className="min-w-[200px]">
              <label className="text-xs theme-text-muted mb-1 block">Fecha</label>
              <DateRangeFilter
                value={fechaRango}
                onChange={setFechaRango}
                label="Fecha"
                compact
              />
            </div>

            <Button
              variant="outline"
              size="icon"
              title="Recargar"
              onClick={loadEntries}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>

            <Button
              variant="outline"
              onClick={clearFiltros}
              disabled={!hasFiltros}
              className="whitespace-nowrap"
            >
              <FilterX className="w-4 h-4 mr-1.5" />
              Limpiar
            </Button>

            <Button
              variant="outline"
              onClick={async () => {
                // Descargamos todas las entries que matchean filtros (max 500)
                // sin paginar — para que el CSV tenga la vista completa
                try {
                  const params = { limit: 500, skip: 0, sort };
                  if (categoria !== "todas") params.categoria = categoria;
                  if (source !== "todas") params.source = source;
                  if (organismo.trim()) params.organismo = organismo.trim();
                  if (search.trim()) params.q = search.trim();
                  const resp = await api.get("/inteligencia-hidrica/entries", { params });
                  const all = resp.data?.entries || [];
                  if (all.length === 0) {
                    toast.warning("No hay entries para exportar.");
                    return;
                  }
                  exportarCSV(all);
                  toast.success(`Exportadas ${all.length} entries a CSV`);
                } catch (e) {
                  toast.error("Error al exportar");
                }
              }}
              disabled={loading}
              className="whitespace-nowrap"
              title="Descarga las entries filtradas como CSV"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Exportar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="theme-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center theme-text-muted">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              Cargando entries del sector agua...
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center theme-text-muted">
              <Droplets className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No se encontraron entries con los filtros actuales.
              {hasFiltros && (
                <div className="text-xs mt-2">
                  Prueba limpiar filtros o ampliar la búsqueda.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b theme-border text-xs uppercase theme-text-muted">
                    <th className="text-left py-3 px-4 w-24">Fecha</th>
                    <th className="text-left py-3 px-4 w-28">Estado</th>
                    <th className="text-left py-3 px-4 w-40 hidden md:table-cell">
                      Categoría
                    </th>
                    <th className="text-left py-3 px-4">Título</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Organismo</th>
                    <th className="text-right py-3 px-4 w-32">Importe</th>
                    <th className="text-center py-3 px-4 w-16 hidden md:table-cell">
                      Score
                    </th>
                    <th className="text-center py-3 px-4 w-12">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const SrcIcon = SOURCE_LABELS[e._source]?.icon || Archive;
                    return (
                      <tr
                        key={`${e._source}-${e._id}`}
                        className="border-b theme-border hover:theme-bg-tertiary transition"
                      >
                        <td className="py-3 px-4 theme-text-muted text-xs font-mono whitespace-nowrap">
                          {formatFecha(e.fecha_referencia)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            className={`text-xs border ${SOURCE_LABELS[e._source]?.className || ""}`}
                          >
                            <SrcIcon className="w-3 h-3 mr-1" />
                            {SOURCE_LABELS[e._source]?.label || e._source}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <Badge
                            variant="outline"
                            className={`text-xs ${CATEGORIA_COLORS[e.categoria_agua] || CATEGORIA_COLORS.OTROS}`}
                          >
                            {(e.categoria_agua || "OTROS").replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="theme-text font-medium max-w-xl line-clamp-2">
                            {e.titulo || "(sin título)"}
                          </div>
                          {e.cpv && (
                            <div className="text-xs theme-text-muted font-mono mt-0.5">
                              CPV: {e.cpv}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 theme-text-muted flex-shrink-0" />
                            <span className="text-xs theme-text truncate max-w-[18rem]">
                              {e.organismo || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm theme-text whitespace-nowrap">
                          {formatEUR(e.importe)}
                        </td>
                        <td className="py-3 px-4 text-center hidden md:table-cell">
                          <Badge
                            variant="outline"
                            className={`font-mono text-xs ${
                              e.score >= 70
                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
                                : e.score >= 40
                                ? "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 border-yellow-500/40"
                                : "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30"
                            }`}
                          >
                            {e.score}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {e.url ? (
                            <a
                              href={e.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex theme-text-muted hover:text-cyan-500"
                              title="Abrir en PLACSP"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          ) : (
                            <span className="theme-text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 print:hidden">
          <div className="text-sm theme-text-muted">
            Mostrando{" "}
            <span className="theme-text font-medium">
              {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, total)}
            </span>{" "}
            de <span className="theme-text font-medium">{total}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
            >
              Anterior
            </Button>
            <span className="text-sm theme-text-muted">
              Página {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
